import { Link } from "react-router-dom";
import { formatCurrency } from "../../../../Utils/formatters";
import { highlightSearchText } from "../highlightSearchText";
import { StatusBadge } from "../StatusBadge";

interface SubAccountItem {
  subAccountId: string;
  name: string;
  idCustomer: string;
  isActive: boolean;
  available: number;
}

interface OrganizerListTableProps {
  data: SubAccountItem[];
  canNavigate?: boolean;
  emptyMessage?: string;
  highlight?: string;
}

const GRID_COLS = "lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1fr)]";

const TableHeader = () => (
  <div
    className={`col-span-4 hidden border-b border-gray-100 bg-gray-50/70 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-300 lg:-mx-4 lg:grid lg:grid-cols-subgrid lg:items-center lg:gap-x-4 lg:px-4 ${GRID_COLS}`}
  >
    <span>Nome</span>
    <span>ID do cliente</span>
    <span className="text-center">Status</span>
    <span className="text-right">Saldo disponível</span>
  </div>
);

const GridCell = ({
  label,
  children,
  align = "left",
}: {
  label: string;
  children: React.ReactNode;
  align?: "left" | "center" | "right";
}) => (
  <div>
    <p
      className={`mb-px text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only ${
        align === "right" ? "text-right" : align === "center" ? "text-center" : ""
      }`}
    >
      {label}
    </p>
    <div
      className={
        align === "right" ? "text-right" : align === "center" ? "flex justify-center" : ""
      }
    >
      {children}
    </div>
  </div>
);

export default function OrganizerListTable({
  data,
  canNavigate,
  emptyMessage,
  highlight,
}: OrganizerListTableProps) {
  if (!data || data.length === 0) {
    return (
      <div className="app-empty-state m-4">
        {emptyMessage || "Nenhum ponto de venda cadastrado."}
      </div>
    );
  }

  return (
    <div className={`lg:grid ${GRID_COLS} lg:gap-x-4 lg:px-4`}>
      <TableHeader />
      {data.map((row) => (
        <div
          key={row.subAccountId}
          className="flex flex-col gap-2 border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-brand-dark-border lg:col-span-4 lg:grid lg:grid-cols-subgrid lg:items-center lg:px-0 lg:py-2.5"
        >
          <GridCell label="Nome">
            {canNavigate ? (
              <Link
                to={`/pontos-de-venda/${row.subAccountId}`}
                className="text-sm font-semibold text-[#c09455] hover:underline dark:text-brand-gold-mid"
              >
                {highlightSearchText(row.name, highlight)}
              </Link>
            ) : (
              <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                {highlightSearchText(row.name, highlight)}
              </p>
            )}
          </GridCell>
          <GridCell label="ID do cliente">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {highlightSearchText(row.idCustomer, highlight)}
            </p>
          </GridCell>
          <GridCell label="Status" align="center">
            <StatusBadge active={row.isActive} />
          </GridCell>
          <GridCell label="Saldo disponível" align="right">
            <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
              {formatCurrency(row.available)}
            </p>
          </GridCell>
        </div>
      ))}
    </div>
  );
}
